import { useState } from "react";
import {
  computeMoodEvidence,
  computeDayComparison,
  pairSessionsWithDailyFallback,
} from "../utils/mood";
import MoodFieldCanvas from "./three/MoodFieldCanvas.jsx";
import "../styles/home-surface.css";
import "./three/mood-field.css";

/**
 * The Evidence Card — the product's whole reason to exist.
 *
 * Shows the user their own within-session mood response instead of telling them
 * exercise is good for mental health. Language is deliberately careful: the
 * headline stat is causal-ish (same day, before vs. after the same session), the
 * secondary day-comparison stat is explicitly labeled as a pattern, not a cause.
 *
 * THE FIELD
 * Above the figure, every paired session is drawn as a rise: a filament of soft
 * light leaving the mood they arrived with and blooming at the mood they left
 * with, over a slow dusk-to-daylight wash. It is the same numbers the sentence
 * underneath states — the art carries the feeling, the text carries the fact,
 * and the band is masked to nothing where the numerals sit so the figure is
 * always read against the plain card.
 *
 * One number, set large and light, with a lot of quiet around it. A big figure
 * in a heavy weight reads as a scoreboard; the same figure at 300 reads as an
 * observation about you.
 */
export default function MoodEvidence({ checkins, workoutDates }) {
  // null = still deciding, "ready" = the field is live, "failed" = CSS only.
  const [fieldStatus, setFieldStatus] = useState(null);

  const ev = computeMoodEvidence(checkins);

  // Not enough paired sessions yet.
  //
  // An earlier pass showed this card from the very first check-in so the field
  // wouldn't be invisible during someone's first week. That was the wrong trade:
  // with nothing paired there is no rise to draw, so it rendered a hero-sized
  // card containing an empty band, the word "gathering" where a figure belongs,
  // and a paragraph explaining what would appear here one day. A card whose
  // entire content is a promise is worse than no card — it takes the most
  // valuable slot on the screen to say nothing.
  //
  // So: nothing renders until there is at least one real before/after pair.
  // From that first pair on, the card is here and grows — each session adds a
  // filament — and it states what it actually has rather than what it needs.
  // No quota, no instructions, no "1 of 3".
  if (!ev.ready) {
    // computeMoodEvidence only returns pairs once it is ready, but the field
    // should already draw the one or two rises that exist.
    const earlyPairs = pairSessionsWithDailyFallback(checkins);
    if (earlyPairs.length === 0) return null;

    return (
      <section className="card card--hero" aria-label="Your mood response to movement">
        <p className="section-label">What movement does for you</p>

        <div className="mf-visual">
          <div
            className="mf-art"
            aria-hidden="true"
            data-live={fieldStatus === null ? undefined : String(fieldStatus === "ready")}
          >
            <div className="mf-wash" />
            <div className="mf-plumes" />
            <MoodFieldCanvas pairs={earlyPairs} onStatusChange={setFieldStatus} />
          </div>

          {/* A count, not an average. One or two sessions is a real thing to
              have done and a terrible thing to draw a conclusion from, so the
              figure reports what exists and the average stays withheld. */}
          <p className="hs-evidence-figure mf-figure">
            <span className="hs-evidence-value">{earlyPairs.length}</span>
            <span className="hs-evidence-unit">
              {earlyPairs.length === 1 ? "session" : "sessions"}
            </span>
          </p>
        </div>

        <p className="ds-body mf-caption">
          Recorded before and after. The shape fills in as you go.
        </p>
      </section>
    );
  }

  const positive = ev.avgDelta > 0;
  const sign = ev.avgDelta > 0 ? "+" : "";

  const rawDayCmp = computeDayComparison(checkins, workoutDates);
  // A day-comparison that contradicts the card's own headline has no business
  // being on it. If the average on days they moved isn't above the average on
  // days they didn't, the honest reading of this cut is "exercise makes me
  // worse" — which is not what the paired before/after figure above says, and
  // is the last sentence a low-mood person should meet in this card. The
  // headline stat already carries the real signal, so this simply doesn't
  // render. (computeDayComparison itself is untouched.)
  const dayCmp =
    rawDayCmp && rawDayCmp.trainingDays > rawDayCmp.restDays ? rawDayCmp : null;

  return (
    <section className="card card--hero" aria-label="Your mood response to movement">
      <p className="section-label">What movement does for you</p>

      {/* Only the art is hidden from assistive tech — every value it draws is
          stated in the text, and the figure below it must stay readable. */}
      <div className="mf-visual">
        <div
          className="mf-art"
          aria-hidden="true"
          data-live={fieldStatus === null ? undefined : String(fieldStatus === "ready")}
        >
          <div className="mf-wash" />
          <div className="mf-plumes" />
          <MoodFieldCanvas pairs={ev.pairs} onStatusChange={setFieldStatus} />
        </div>

        <p className="hs-evidence-figure mf-figure">
          <span className="hs-evidence-value">
            {sign}
            {ev.avgDelta}
          </span>
          <span className="hs-evidence-unit">mood</span>
        </p>
      </div>

      {/* At most one bolded figure per sentence, and only where the sentence
          turns on it. Five bolded numerals in three short lines under a display
          figure reads as a stat block, not as an observation about a person. */}
      <p className="ds-body mf-caption">
        {positive ? (
          <>
            Average lift from before to after, across {ev.sessions} sessions. You came
            away feeling better {ev.improved} {ev.improved === 1 ? "time" : "times"}.
          </>
        ) : (
          <>
            Across {ev.sessions} sessions your mood hasn&apos;t shifted much yet.
            That&apos;s worth knowing too — it often takes a few weeks.
          </>
        )}
      </p>

      {/* The most meaningful cut: what happened on the days it was hardest. */}
      {ev.roughDayReady && ev.roughDayAvgDelta > 0 && (
        <div className="ds-well--accent" style={{ marginTop: "var(--stack)" }}>
          <p className="ds-body">
            On the {ev.roughDaySessions} days you started out low, your reading
            afterwards was <strong>+{ev.roughDayAvgDelta}</strong> higher. Those
            are the days it matters most.
          </p>
        </div>
      )}

      {dayCmp && (
        <div className="hs-evidence-note">
          <p className="ds-support">
            {/* Both figures to the same precision — "2.2 vs 3" reads as
                broken as well as discouraging. */}
            On days you move, your mood averages{" "}
            <strong>{Number(dayCmp.trainingDays).toFixed(1)}</strong>. On days you
            don&apos;t, <strong>{Number(dayCmp.restDays).toFixed(1)}</strong>.
          </p>
          <p className="ds-meta" style={{ marginTop: "var(--space-2)" }}>
            A pattern, not proof — you may also move more on days you already feel good.
          </p>
        </div>
      )}
    </section>
  );
}
